import React, { useRef, useState } from 'react';
import { Upload, Check, FileText, Loader2, CheckCircle2, MessageSquareWarning, Paperclip } from 'lucide-react';
import { portal } from '../../api';

const DocItem = ({ token, req, item, onDone }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const received = item.status === 'received';
  const pick = (file) => {
    setBusy(true); setError('');
    portal.uploadItem(token, req.id, item.id, file).then(onDone).catch((e) => setError(e?.response?.data?.detail || 'Upload failed')).finally(() => setBusy(false));
  };
  return (
    <div className="flex items-center gap-3 px-5 py-4" data-testid={`portal-doc-item-${item.id}`}>
      <div className={`h-8 w-8 rounded-full grid place-items-center flex-shrink-0 ${received ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-50 text-amber-700'}`}>{received ? <Check size={15}/> : <FileText size={15}/>}</div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-stone-900 text-sm">{item.label}</div>
        <div className="text-xs text-stone-500">{received ? <>Received{item.fileId && <> · <a href={portal.fileUrl(token, item.fileId)} target="_blank" rel="noreferrer" className="text-emerald-800 hover:underline">{item.fileName}</a></>}</> : 'Still needed'}</div>
        {error && <div className="text-xs text-red-600 mt-0.5">{error}</div>}
      </div>
      <input ref={ref} type="file" className="hidden" onChange={(e) => { if (e.target.files?.[0]) pick(e.target.files[0]); e.target.value = ''; }}/>
      <button type="button" disabled={busy} onClick={() => ref.current?.click()} data-testid={`portal-doc-upload-${item.id}`}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${received ? 'border border-stone-200 text-stone-600 hover:bg-stone-50' : 'bg-emerald-800 text-white hover:bg-emerald-900'}`}>
        {busy ? <Loader2 size={13} className="animate-spin"/> : <Upload size={13}/>} {received ? 'Replace' : 'Upload'}
      </button>
    </div>
  );
};

export const PortalDocuments = ({ token, requests, onChange }) => (
  <section className="space-y-6" data-testid="section-documents">
    {requests.length === 0 && <div className="rounded-xl bg-white border border-stone-200 p-10 text-center text-sm text-stone-500">No documents requested right now.</div>}
    {requests.map((r, idx) => {
      const left = r.items.filter((i) => i.status !== 'received').length;
      return (
        <div key={r.id} className="rounded-xl bg-white border border-stone-200 overflow-hidden" data-testid={`portal-doc-request-${r.id}`}>
          <div className="p-5 border-b border-stone-100 flex items-center justify-between gap-3">
            <div>
              <div className="font-semibold text-stone-900">Documents for {r.periodLabel}{idx === 0 && <span className="ml-2 text-[10px] uppercase tracking-wider font-bold text-emerald-700">Current</span>}</div>
              <div className="text-xs text-stone-500 mt-0.5">PDF, images, Word, Excel or CSV · up to 10 MB each</div>
            </div>
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${left ? 'bg-amber-50 text-amber-800' : 'bg-emerald-50 text-emerald-800'}`}>{left ? `${left} still needed` : 'All received'}</span>
          </div>
          <div className="divide-y divide-stone-100">{r.items.map((it) => <DocItem key={it.id} token={token} req={r} item={it} onDone={onChange}/>)}</div>
        </div>
      );
    })}
  </section>
);

const ApprovalCard = ({ token, s, onDone }) => {
  const [name, setName] = useState('');
  const [comment, setComment] = useState('');
  const [mode, setMode] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = (action) => {
    setBusy(true); setError('');
    portal.respond(token, s.id, { action, name, comment }).then(onDone).catch((e) => setError(e?.response?.data?.detail || 'Could not submit')).finally(() => setBusy(false));
  };
  const done = s.status !== 'pending';
  return (
    <div className="rounded-xl bg-white border border-stone-200 p-5" data-testid={`portal-signoff-${s.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-semibold text-stone-900">{s.title}</div>
          {s.description && <div className="text-sm text-stone-600 mt-1">{s.description}</div>}
          {s.fileId && <a href={portal.fileUrl(token, s.fileId)} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-sm text-emerald-800 hover:underline"><Paperclip size={13}/> {s.fileName}</a>}
        </div>
        {done && (
          <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${s.status === 'approved' ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-700'}`} data-testid={`portal-signoff-status-${s.id}`}>
            {s.status === 'approved' ? <CheckCircle2 size={13}/> : <MessageSquareWarning size={13}/>} {s.status === 'approved' ? 'Approved' : 'Changes requested'}
          </span>
        )}
      </div>
      {done && s.response && <div className="mt-3 text-xs text-stone-500">By {s.response.name} on {new Date(s.response.at).toLocaleString()}{s.response.comment && <> · &ldquo;{s.response.comment}&rdquo;</>}</div>}
      {!done && (
        <div className="mt-4 space-y-3">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your full name" className="w-full h-10 rounded-md border border-stone-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" data-testid={`portal-signoff-name-${s.id}`}/>
          {mode === 'changes' && (
            <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder="What needs to change?" className="w-full rounded-md border border-stone-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" data-testid={`portal-signoff-comment-${s.id}`}/>
          )}
          {error && <div className="text-xs text-red-600" data-testid={`portal-signoff-error-${s.id}`}>{error}</div>}
          <div className="flex gap-2 flex-wrap">
            <button type="button" disabled={busy} onClick={() => submit('approve')} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-800 hover:bg-emerald-900 text-white text-sm font-medium" data-testid={`portal-signoff-approve-${s.id}`}>
              {busy ? <Loader2 size={14} className="animate-spin"/> : <CheckCircle2 size={14}/>} Approve
            </button>
            <button type="button" disabled={busy} onClick={() => (mode === 'changes' ? submit('changes') : setMode('changes'))} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-stone-300 text-stone-700 hover:bg-stone-50 text-sm font-medium" data-testid={`portal-signoff-changes-${s.id}`}>
              <MessageSquareWarning size={14}/> {mode === 'changes' ? 'Submit changes request' : 'Request changes'}
            </button>
          </div>
          <div className="text-[11px] text-stone-400">Your name, the date and time, and your IP address are recorded with your response.</div>
        </div>
      )}
    </div>
  );
};

export const PortalApprovals = ({ token, signoffs, onChange }) => (
  <section className="space-y-4" data-testid="section-approvals">
    {signoffs.length === 0 && <div className="rounded-xl bg-white border border-stone-200 p-10 text-center text-sm text-stone-500">Nothing waiting for your approval.</div>}
    {signoffs.map((s) => <ApprovalCard key={s.id} token={token} s={s} onDone={onChange}/>)}
  </section>
);
